import {
  Box,
  CircularProgress,
  IconButton,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material';
import { Pencil, Save } from 'lucide-react';
import { useContext, useEffect, useState } from 'react';
import '../template.module.css';

import { RemoveBlockSVG } from './HelperSVGs';
import { ReportTemplatesContext } from '..';

export default function DefaultBlock({
  id,
  index,
  sectionKey,
  block,
}) {
  const {
    removeBlock,
    updateBlockTitle,
    updatedBlockPrefixAndSuffix,
    updateBlockValue,
    getAccessToken,
  } = useContext(ReportTemplatesContext);
  const [edit, setEdit] = useState(false);
  const [title, setTitle] = useState(block.title);
  const [valueLoader, setValueLoader] = useState(true);
  const [blockValue, setBlockValue] = useState(null);
  const [prefix, setPrefix] = useState(block.prefix || 'None');
  const [suffix, setSuffix] = useState(block.suffix || 'None');
  const [customPrefix, setCustomPrefix] = useState(
    block.prefix && !['$', '€', '£', 'None'].includes(block.prefix)
      ? block.prefix
      : '',
  );
  const [customSuffix, setCustomSuffix] = useState(
    block.suffix && !['%', 'x', 'None'].includes(block.suffix)
      ? block.suffix
      : '',
  );
  const prefixOptions =
    suffix === '%' ? ['Custom', 'None'] : ['$', '€', '£', 'Custom', 'None'];
  const suffixOptions = ['$', '€', '£'].includes(prefix)
    ? ['Custom', 'None']
    : ['%', 'x', 'Custom', 'None'];

  const formatNumber = value => {
    if (isNaN(value)) return value; // Return if not a number
    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);
  };

  const handleSave = () => {
    updateBlockTitle(block.title, title, sectionKey);
    setEdit(false);
  };

  useEffect(() => {
    async function getBlockValue() {
      try {
        const url = process.env.REACT_APP_NODE_URL + '/templates/block-value';
        const response = await fetch(url, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${getAccessToken()}`,
          },
          body: JSON.stringify({ block }),
        });

        if (!response.ok) {
          throw new Error('Network response was not ok');
        }

        const data = await response.json();
        setBlockValue(data.value);
        updateBlockValue(block.title, sectionKey, data.value, block?.group);
        setValueLoader(false);
      } catch (error) {
        console.error(error);
        setValueLoader(false);
      }
    }

    if (block) {
      if (!block?.templateValue) {
        getBlockValue();
      } else {
        setBlockValue(block.templateValue);
        setValueLoader(false);
      }
    }
  }, []);

  useEffect(() => {
    const appliedPrefix = prefix === 'Custom' ? customPrefix : prefix;
    const appliedSuffix = suffix === 'Custom' ? customSuffix : suffix;

    updatedBlockPrefixAndSuffix(
      block.title,
      sectionKey,
      appliedPrefix,
      appliedSuffix,
    );
  }, [prefix, suffix, customPrefix, customSuffix]);

  return (
    <Box
      sx={{
        flex: 1,
        backgroundColor: '#F9F9F9',
        paddingY: '1.25rem',
        paddingX: '1rem',
        borderRadius: '1rem',
        display: 'flex',
        gap: '1rem',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      {block?.group && (
        <Typography
          sx={{
            fontFamily: 'Lato',
            lineHeight: '1.5rem',
            textAlign: 'center',
            fontSize: '1.1rem',
            fontWeight: 700,
            color: '#39585C',
          }}
        >
          {block.group}
        </Typography>
      )}
      <Box sx={{ display: 'flex', gap: '.65rem', alignItems: 'center' }}>
        {edit ? (
          <TextField
            value={title}
            onChange={e => setTitle(e.target.value)}
            size='small'
            sx={{ width: '150px' }}
          />
        ) : (
          <Typography
            sx={{
              fontFamily: 'Lato',
              lineHeight: '1.5rem',
              textAlign: 'center',
              fontSize: '1.1rem',
              fontWeight: 500,
              color: '#39585C',
            }}
          >
            {block.title}
          </Typography>
        )}
        <IconButton onClick={() => (edit ? handleSave() : setEdit(true))}>
          {edit ? (
            <Save height={18} width={18} color='#39585C' />
          ) : (
            <Pencil height={18} width={18} color='#39585C' />
          )}
        </IconButton>
      </Box>

      {valueLoader ? (
        <CircularProgress />
      ) : (
        <Typography
          sx={{
            fontFamily: 'Lato',
            lineHeight: '1.5rem',
            textAlign: 'center',
            fontSize: '1.4rem',
            fontWeight: 900,
          }}
        >
          {prefix === 'Custom'
            ? customPrefix
            : prefix === 'None'
              ? null
              : prefix}
          {blockValue !== '' || blockValue !== null
            ? formatNumber(blockValue)
            : 'value'}
          {suffix === 'Custom'
            ? customSuffix
            : suffix === 'None'
              ? null
              : suffix}
        </Typography>
      )}

      <Typography
        sx={{
          fontFamily: 'Lato',
          lineHeight: '1.5rem',
          textAlign: 'center',
          fontSize: '1rem',
          fontWeight: 500,
        }}
      >
        National Average
      </Typography>

      <Box sx={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
        {/* Prefix Dropdown */}
        <TextField
          select
          label='Prefix'
          value={prefix}
          onChange={e => setPrefix(e.target.value)}
          sx={{ minWidth: '100px' }}
        >
          {prefixOptions.map(option => (
            <MenuItem key={option} value={option}>
              {option}
            </MenuItem>
          ))}
        </TextField>

        {prefix === 'Custom' && (
          <TextField
            label='Custom Prefix'
            value={customPrefix}
            onChange={e => {
              setCustomPrefix(e.target.value);
            }}
            size='small'
            sx={{ minWidth: '100px' }}
          />
        )}

        {/* Suffix Dropdown */}
        <TextField
          select
          label='Suffix'
          value={suffix}
          onChange={e => setSuffix(e.target.value)}
          sx={{ minWidth: '100px' }}
        >
          {suffixOptions.map(option => (
            <MenuItem key={option} value={option}>
              {option}
            </MenuItem>
          ))}
        </TextField>

        {suffix === 'Custom' && (
          <TextField
            label='Custom Suffix'
            value={customSuffix}
            onChange={e => {
              setCustomSuffix(e.target.value);
            }}
            size='small'
            sx={{ minWidth: '100px' }}
          />
        )}
      </Box>

      <Box
        sx={{
          cursor: 'pointer',
          alignSelf: 'center',
        }}
        onClick={() => {
          removeBlock(block.title, sectionKey, block?.group);
        }}
      >
        <RemoveBlockSVG />
      </Box>
    </Box>
  );
}
